import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { string, func } from "prop-types";
import cogoToast from "cogo-toast";
import httpRequest from "../../ajax";
import "./index.scss";

const JobPostingVerificationBanner = ({ initialEmail, onDismiss }) => {
  const history = useHistory();
  const [status, setStatus] = useState(initialEmail ? "pending" : null); // 'pending' | 'verified' | null
  const [companyEmail, setCompanyEmail] = useState(initialEmail || "");
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem("job_posting_verification_banner_dismissed") === "true";
  });
  const [resending, setResending] = useState(false);
  const isMountedRef = useRef(true);

  // Check verification status from API
  const checkStatus = useCallback(async () => {
    try {
      const res = await httpRequest("get", "/employer-verification/status");
      if (!isMountedRef.current) return;

      const data = res?.data?.data;
      if (!data) return;

      const email = data.company_email || initialEmail || "";
      if (email) {
        setCompanyEmail(email);
      }

      if (data.company_email_verified || data.verification_status === "approved") {
        setStatus((prev) => (prev === "pending" ? "verified" : null));
      } else if (data.verification_status === "pending" && !data.company_email_verified) {
        setStatus("pending");
      } else {
        setStatus(null);
      }
    } catch {
      // Suppress network check errors silently
    }
  }, [initialEmail]);

  useEffect(() => {
    isMountedRef.current = true;

    if (initialEmail) {
      setCompanyEmail(initialEmail);
      setStatus("pending");
      setDismissed(false);
      sessionStorage.removeItem("job_posting_verification_banner_dismissed");
    }

    checkStatus();

    return () => {
      isMountedRef.current = false;
    };
  }, [initialEmail, checkStatus]);

  // Polling & focus listener while pending
  useEffect(() => {
    if (status !== "pending") return;

    const handleFocus = () => {
      checkStatus();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);

    const intervalId = setInterval(() => {
      checkStatus();
    }, 15000);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
      clearInterval(intervalId);
    };
  }, [status, checkStatus]);

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const res = await httpRequest("post", "/employer-verification/resend-company-email");
      if (res?.data?.status) {
        cogoToast.success(
          res?.data?.message || "Verification email sent. Please check your company inbox."
        );
      } else {
        cogoToast.error(res?.data?.message || "Failed to resend verification email.");
      }
    } catch (err) {
      cogoToast.error(
        err?.response?.data?.message || "Failed to resend verification email. Please try again."
      );
    } finally {
      if (isMountedRef.current) {
        setResending(false);
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("job_posting_verification_banner_dismissed", "true");
    if (onDismiss) onDismiss();
  };

  const handleViewJobs = () => {
    handleDismiss();
    history.push("/myjobs");
  };

  if (dismissed || !status) return null;

  return (
    <div
      className={`job-posting-verification-banner country-missing-banner ${
        status === "verified" ? "job-posting-verification-banner--verified" : ""
      }`}
      role="alert"
    >
      <div className="banner-content">
        {status === "pending" ? (
          <svg
            className="banner-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : (
          <svg
            className="banner-icon banner-icon--verified"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}

        <div className="banner-text">
          {status === "pending" ? (
            <>
              <strong>Verification Required:</strong>
              <span>
                Please check your inbox at{" "}
                {companyEmail ? <strong>{companyEmail}</strong> : "your work email"}{" "}
                and verify your account to publish your job.
              </span>
            </>
          ) : (
            <>
              <strong>Job Published:</strong>
              <span>
                Your work email has been verified and your job is now live.
              </span>
            </>
          )}
        </div>
      </div>

      <div className="banner-actions">
        {status === "pending" ? (
          <button
            type="button"
            className="banner-cta-btn"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? "Sending..." : "Resend Email"}
          </button>
        ) : (
          <button
            type="button"
            className="banner-cta-btn banner-cta-btn--verified"
            onClick={handleViewJobs}
          >
            View Jobs
          </button>
        )}

        <button
          type="button"
          className="banner-close-btn"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
        >
          <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

JobPostingVerificationBanner.propTypes = {
  initialEmail: string,
  onDismiss: func,
};

JobPostingVerificationBanner.defaultProps = {
  initialEmail: "",
  onDismiss: null,
};

export default JobPostingVerificationBanner;
